import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ComposedChart,
  ReferenceLine,
  Cell
} from 'recharts';
import { RefreshCw, LogOut } from 'lucide-react';
import { Navigation } from './Navigation';
import InfoTooltip from './InfoTooltip';

// SectionCard component matching Dashboard style
const SectionCard = ({ title, children, tooltip }) => (
  <div className="bg-[#1A1A1A] rounded-lg p-4 card-shadow h-full">
    <div className="flex items-center gap-2 mb-3 pb-2 border-b border-white/10">
      <h3 className="text-sm font-medium text-white">{title}</h3>
      {tooltip}
    </div>
    {children}
  </div>
);

// Format number with Danish locale
const formatDKK = (value) => {
  if (value === null || value === undefined) return 'N/A';
  return Math.round(value).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
};

// Stable chart container
const StableChartContainer = ({ children, height = 200 }) => (
  <div style={{ width: '100%', height }}>
    <ResponsiveContainer width="100%" height="100%">
      {children}
    </ResponsiveContainer>
  </div>
);

export const HistoricalData = ({ user }) => {
  const navigate = useNavigate();
  const [refreshing, setRefreshing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [historicalData, setHistoricalData] = useState(null);
  const [cancellationData, setCancellationData] = useState(null);
  
  // Date range state - default to last 12 weeks
  const today = new Date().toISOString().split('T')[0];
  const twelveWeeksAgo = new Date(Date.now() - 84 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
  const [startDate, setStartDate] = useState(twelveWeeksAgo);
  const [endDate, setEndDate] = useState(today);
  const [region, setRegion] = useState('Hele landet');
  const [regions] = useState(['Hele landet', 'Sjælland', 'Fyn', 'Jylland']);
  const [viewMode, setViewMode] = useState('uge'); // 'uge' or 'måned'

  // Fetch data
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [historicalRes, cancellationRes] = await Promise.all([
        api.get('/historical/combined', {
          params: {
            start_date: startDate,
            end_date: endDate,
            region: region
          }
        }),
        api.get('/historical/cancellations', {
          params: {
            start_date: startDate,
            end_date: endDate,
            region: region
          }
        })
      ]);
      setHistoricalData(historicalRes.data);
      setCancellationData(cancellationRes.data);
    } catch (err) {
      console.error('Error fetching historical data:', err);
      if (err.response?.status === 401) {
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, region, navigate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchData();
    setRefreshing(false);
  };

  const handleLogout = async () => {
    try {
      await api.post('/auth/logout');
      navigate('/login');
    } catch (err) {
      navigate('/login');
    }
  };

  // Memoized chart data
  const weeklyData = useMemo(() => historicalData?.weekly_data || [], [historicalData]);
  const summary = useMemo(() => historicalData?.summary || {}, [historicalData]);
  const cancellationWeeklyData = useMemo(() => cancellationData?.weekly_data || [], [cancellationData]);
  const cancellationSummary = useMemo(() => cancellationData?.summary || {}, [cancellationData]);

  // Aggregate weekly data into monthly data
  const monthlyData = useMemo(() => {
    if (!weeklyData.length) return [];
    const monthMap = {};
    const danishMonths = ['Jan', 'Feb', 'Mar', 'Apr', 'Maj', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dec'];
    for (const week of weeklyData) {
      const weekStart = new Date(week.week_start);
      const monthKey = `${weekStart.getFullYear()}-${String(weekStart.getMonth() + 1).padStart(2, '0')}`;
      if (!monthMap[monthKey]) {
        monthMap[monthKey] = {
          month_key: monthKey,
          month_label: `${danishMonths[weekStart.getMonth()]} ${weekStart.getFullYear()}`,
          year: weekStart.getFullYear(),
          month: weekStart.getMonth() + 1,
          spend: 0, nye_bookinger: 0, afviklede_bookinger: 0,
          aflysninger: 0, total_bookinger_for_aflysning: 0,
          business_hours_min: 0, daekning: 0, ordre_count: 0, omsaetning: 0
        };
      }
      const m = monthMap[monthKey];
      m.spend += week.spend || 0;
      m.nye_bookinger += week.nye_bookinger || 0;
      m.afviklede_bookinger += week.afviklede_bookinger || 0;
      m.aflysninger += week.aflysninger || 0;
      m.total_bookinger_for_aflysning += week.total_bookinger_for_aflysning || 0;
      m.business_hours_min += (week.timer || 0) * 60;
      m.daekning += week.daekning || 0;
      m.ordre_count += week.ordre_count || 0;
      m.omsaetning += week.omsaetning || 0;
    }
    return Object.values(monthMap).sort((a, b) => a.month_key.localeCompare(b.month_key)).map(m => {
      const hours = m.business_hours_min / 60;
      return {
        ...m,
        omsaetning: Math.round(m.omsaetning),
        timer: Math.round(hours * 10) / 10,
        aflysning_pct: m.total_bookinger_for_aflysning > 0 ? Math.round(m.aflysninger / m.total_bookinger_for_aflysning * 1000) / 10 : 0,
        pris_pr_booking: m.nye_bookinger > 0 ? Math.round(m.spend / m.nye_bookinger * 100) / 100 : 0,
        pris_pr_afviklet: m.afviklede_bookinger > 0 ? Math.round(m.spend / m.afviklede_bookinger * 100) / 100 : 0,
        gns_daekning: m.ordre_count > 0 ? Math.round(m.daekning / m.ordre_count * 100) / 100 : 0,
        gns_daekning_pr_ny_booking: m.nye_bookinger > 0 ? Math.round(m.daekning / m.nye_bookinger * 100) / 100 : 0,
        gns_daekning_pr_afviklet: m.afviklede_bookinger > 0 ? Math.round(m.daekning / m.afviklede_bookinger * 100) / 100 : 0,
        kpt: hours > 0 ? Math.round(m.afviklede_bookinger / hours * 100) / 100 : 0,
        dpt: hours > 0 ? Math.round(m.daekning / hours * 100) / 100 : 0,
      };
    });
  }, [weeklyData]);

  // Aggregate cancellation data into monthly
  const cancellationMonthlyData = useMemo(() => {
    if (!cancellationWeeklyData.length) return [];
    const monthMap = {};
    const danishMonths = ['Jan', 'Feb', 'Mar', 'Apr', 'Maj', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dec'];
    for (const week of cancellationWeeklyData) {
      const weekStart = new Date(week.week_start);
      const monthKey = `${weekStart.getFullYear()}-${String(weekStart.getMonth() + 1).padStart(2, '0')}`;
      if (!monthMap[monthKey]) {
        monthMap[monthKey] = {
          month_key: monthKey,
          month_label: `${danishMonths[weekStart.getMonth()]} ${weekStart.getFullYear()}`,
          cancelled: 0, total: 0
        };
      }
      monthMap[monthKey].cancelled += week.cancelled_bookings || 0;
      monthMap[monthKey].total += week.total_bookings || 0;
    }
    return Object.values(monthMap).sort((a, b) => a.month_key.localeCompare(b.month_key)).map(m => ({
      ...m,
      cancellation_rate: m.total > 0 ? Math.round(m.cancelled / m.total * 1000) / 10 : 0
    }));
  }, [cancellationWeeklyData]);

  // Active data based on view mode
  const chartData = viewMode === 'uge' ? weeklyData : monthlyData;
  const labelKey = viewMode === 'uge' ? 'week_label' : 'month_label';
  const periodLabel = viewMode === 'uge' ? 'uge' : 'måned';
  const cancellationChartData = viewMode === 'uge' ? cancellationWeeklyData : cancellationMonthlyData;
  const cancellationLabelKey = viewMode === 'uge' ? 'week_label' : 'month_label';

  // Calculate averages for trend lines
  const averages = useMemo(() => {
    if (!chartData.length) return {};
    
    const calcAvg = (arr, key) => {
      const values = arr.map(d => d[key]).filter(v => v !== null && v !== undefined && !isNaN(v));
      return values.length ? values.reduce((a, b) => a + b, 0) / values.length : 0;
    };
    
    return {
      omsaetning: calcAvg(chartData, 'omsaetning'),
      daekning: calcAvg(chartData, 'daekning'),
      dg: (() => {
        const totalDaekning = chartData.reduce((s, w) => s + (w.daekning || 0), 0);
        const totalOmsaetning = chartData.reduce((s, w) => s + (w.omsaetning || 0), 0);
        return totalOmsaetning > 0 ? (totalDaekning / totalOmsaetning) * 100 : 0;
      })(),
      spend: calcAvg(chartData, 'spend'),
      nye_bookinger: calcAvg(chartData, 'nye_bookinger'),
      afviklede_bookinger: calcAvg(chartData, 'afviklede_bookinger'),
      pris_pr_booking: calcAvg(chartData, 'pris_pr_booking'),
      pris_pr_afviklet: calcAvg(chartData, 'pris_pr_afviklet'),
      gns_daekning: calcAvg(chartData, 'gns_daekning'),
      gns_daekning_pr_ny_booking: calcAvg(chartData, 'gns_daekning_pr_ny_booking'),
      gns_daekning_pr_afviklet: calcAvg(chartData, 'gns_daekning_pr_afviklet'),
      konsulent_timer: calcAvg(chartData, 'timer'),
      dpt: (() => {
        const totalDB = chartData.reduce((s, w) => s + (w.daekning || 0), 0);
        const totalHours = chartData.reduce((s, w) => s + (w.timer || 0), 0);
        return totalHours > 0 ? totalDB / totalHours : 0;
      })(),
      kpt: calcAvg(chartData, 'kpt'),
      cancellation_rate: calcAvg(cancellationChartData, 'cancellation_rate')
    };
  }, [chartData, cancellationChartData]);

  return (
    <div className="min-h-screen bg-[#121212]" data-testid="historical-data">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-[#121212]/95 backdrop-blur-md border-b border-white/5">
        <div className="max-w-[1920px] mx-auto px-6 py-4">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-4">
              <Navigation />
              <h1 className="text-xl font-semibold text-white">Historisk Data</h1>
            </div>
            
            <div className="flex items-center gap-3 flex-wrap">
              {/* Date Range */}
              <div className="flex items-center gap-2">
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="bg-[#1A1A1A] border border-white/10 text-white text-sm px-3 py-2 rounded-lg focus:outline-none focus:border-[#1E6F7A]"
                />
                <span className="text-white/40">-</span>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="bg-[#1A1A1A] border border-white/10 text-white text-sm px-3 py-2 rounded-lg focus:outline-none focus:border-[#1E6F7A]"
                />
              </div>
              
              {/* View Mode Toggle */}
              <div className="flex items-center bg-[#1A1A1A] border border-white/10 rounded-lg overflow-hidden" data-testid="view-mode-toggle">
                <button
                  onClick={() => setViewMode('uge')}
                  className={`px-4 py-2 text-sm font-medium transition-colors ${viewMode === 'uge' ? 'bg-[#1E6F7A] text-white' : 'text-white/60 hover:text-white'}`}
                  data-testid="view-mode-uge"
                >
                  Pr. uge
                </button>
                <button
                  onClick={() => setViewMode('måned')}
                  className={`px-4 py-2 text-sm font-medium transition-colors ${viewMode === 'måned' ? 'bg-[#1E6F7A] text-white' : 'text-white/60 hover:text-white'}`}
                  data-testid="view-mode-maaned"
                >
                  Pr. måned
                </button>
              </div>
              
              {/* Region */}
              <select
                value={region}
                onChange={(e) => setRegion(e.target.value)}
                className="bg-[#1A1A1A] border border-white/10 text-white text-sm px-4 py-2 rounded-lg focus:outline-none focus:border-[#1E6F7A]"
              >
                {regions.map((r) => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
              
              <button
                onClick={handleRefresh}
                disabled={refreshing || loading}
                className="p-2 bg-[#1A1A1A] border border-white/10 text-white/60 hover:text-white rounded-lg transition-colors disabled:opacity-50"
              >
                <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              </button>
              <button
                onClick={handleLogout}
                className="p-2 bg-[#1A1A1A] border border-white/10 text-white/60 hover:text-[#B3261E] rounded-lg transition-colors"
              >
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-[1920px] mx-auto px-6 py-6 space-y-6">
        
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-white/60">Indlæser historiske data...</div>
          </div>
        ) : (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-5 lg:grid-cols-11 gap-4">
              <div className="bg-[#1A1A1A] rounded-lg p-4 border-l-4 border-[#C77700]">
                <p className="text-xs text-white/60 mb-1">Samlet Omsætning</p>
                <p className="text-xl font-bold text-[#C77700]">{formatDKK(summary.total_omsaetning)} kr</p>
                <p className="text-xs text-white/30 mt-1">excl. moms</p>
              </div>
              <div className="bg-[#1A1A1A] rounded-lg p-4 border-l-4 border-[#1E6F7A]">
                <p className="text-xs text-white/60 mb-1">Samlet Dækning</p>
                <p className="text-xl font-bold text-[#1E6F7A]">{formatDKK(summary.total_daekning)} kr</p>
              </div>
              <div className="bg-[#1A1A1A] rounded-lg p-4">
                <p className="text-xs text-white/60 mb-1">Total Spend</p>
                <p className="text-xl font-bold text-[#C77700]">{formatDKK(summary.total_spend)} kr</p>
              </div>
              <div className="bg-[#1A1A1A] rounded-lg p-4">
                <p className="text-xs text-white/60 mb-1">Nye Bookinger</p>
                <p className="text-xl font-bold text-white">{summary.total_nye_bookinger || 0}</p>
              </div>
              <div className="bg-[#1A1A1A] rounded-lg p-4">
                <p className="text-xs text-white/60 mb-1">Afviklede</p>
                <p className="text-xl font-bold text-white">{summary.total_afviklede || 0}</p>
              </div>
              <div className="bg-[#1A1A1A] rounded-lg p-4">
                <p className="text-xs text-white/60 mb-1">Gns. Pris/Ny Booking</p>
                <p className="text-xl font-bold" style={{ color: summary.avg_pris_pr_booking <= 1200 ? '#2E7D32' : summary.avg_pris_pr_booking <= 1500 ? '#C77700' : '#B3261E' }}>
                  {formatDKK(summary.avg_pris_pr_booking)} kr
                </p>
              </div>
              <div className="bg-[#1A1A1A] rounded-lg p-4">
                <p className="text-xs text-white/60 mb-1">Gns. Pris/Afviklet</p>
                <p className="text-xl font-bold" style={{ color: (summary.avg_pris_pr_afviklet || 0) <= 1200 ? '#2E7D32' : (summary.avg_pris_pr_afviklet || 0) <= 1500 ? '#C77700' : '#B3261E' }}>
                  {formatDKK(summary.avg_pris_pr_afviklet || 0)} kr
                </p>
              </div>
              <div className="bg-[#1A1A1A] rounded-lg p-4">
                <p className="text-xs text-white/60 mb-1">Gns. Dækn./Ny Book.</p>
                <p className="text-xl font-bold" style={{ color: (summary.avg_daekning_pr_ny_booking || 0) >= 4500 ? '#2E7D32' : (summary.avg_daekning_pr_ny_booking || 0) >= 3500 ? '#C77700' : '#B3261E' }}>
                  {formatDKK(summary.avg_daekning_pr_ny_booking || 0)} kr
                </p>
              </div>
              <div className="bg-[#1A1A1A] rounded-lg p-4">
                <p className="text-xs text-white/60 mb-1">Gns. Dækn./Afviklet</p>
                <p className="text-xl font-bold" style={{ color: (summary.avg_daekning_pr_afviklet || 0) >= 4500 ? '#2E7D32' : (summary.avg_daekning_pr_afviklet || 0) >= 3500 ? '#C77700' : '#B3261E' }}>
                  {formatDKK(summary.avg_daekning_pr_afviklet || 0)} kr
                </p>
              </div>
              <div className="bg-[#1A1A1A] rounded-lg p-4">
                <p className="text-xs text-white/60 mb-1">Gns. KPT</p>
                <p className="text-xl font-bold" style={{ color: summary.avg_kpt >= 0.45 ? '#2E7D32' : summary.avg_kpt >= 0.35 ? '#C77700' : '#B3261E' }}>
                  {(summary.avg_kpt || 0).toFixed(2)}
                </p>
              </div>
              <div className="bg-[#1A1A1A] rounded-lg p-4">
                <p className="text-xs text-white/60 mb-1">Aflysning %</p>
                <p className="text-xl font-bold" style={{ color: cancellationSummary.cancellation_rate <= 5 ? '#2E7D32' : cancellationSummary.cancellation_rate <= 10 ? '#C77700' : '#B3261E' }}>
                  {(cancellationSummary.cancellation_rate || 0).toFixed(1)}%
                </p>
                <p className="text-xs text-white/40 mt-1">{cancellationSummary.cancelled_bookings || 0} / {cancellationSummary.total_bookings || 0}</p>
              </div>
            </div>

            {/* Charts Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              
              {/* Omsætning pr. uge/måned - FIRST */}
              <SectionCard 
                title={`Omsætning pr. ${periodLabel}`}
                tooltip={
                  <InfoTooltip
                    title="Omsætning pr. uge"
                    description="Viser den samlede omsætning (excl. moms) for hver uge/måned i den valgte periode."
                    formula="Omsætning = order_grand_total / 1,25 (excl. 25% moms)"
                    details={[
                      "Baseret på ordrer fra Data_klar",
                      "Beløb er excl. moms (25%)",
                      "Lilla linje = Gennemsnit for perioden"
                    ]}
                    dataSource="Data_klar (kolonne E: order_grand_total)"
                  />
                }
              >
                <StableChartContainer height={200}>
                  <ComposedChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                    <XAxis dataKey={labelKey} stroke="rgba(255,255,255,0.4)" tick={{ fill: 'rgba(255,255,255,0.6)', fontSize: 10 }} />
                    <YAxis stroke="rgba(255,255,255,0.4)" tick={{ fill: 'rgba(255,255,255,0.6)', fontSize: 10 }} tickFormatter={(v) => `${(v/1000).toFixed(0)}k`} />
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#1A1A1A', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: '#fff' }}
                      labelStyle={{ color: '#fff' }}
                      formatter={(value) => [`${formatDKK(value)} kr`, 'Omsætning']}
                    />
                    <ReferenceLine y={averages.omsaetning} stroke="#8B5CF6" strokeDasharray="4 4" label={{ value: `Gns: ${formatDKK(averages.omsaetning)}`, fill: '#8B5CF6', fontSize: 10, position: 'left' }} />
                    <Bar dataKey="omsaetning" fill="#C77700" radius={[4, 4, 0, 0]} name="Omsætning" />
                  </ComposedChart>
                </StableChartContainer>
              </SectionCard>

              {/* Dækning pr. uge */}
              <SectionCard 
                title={`Dækning pr. ${periodLabel}`}
                tooltip={
                  <InfoTooltip
                    title="Dækning pr. uge"
                    description="Viser den samlede dækning (bruttofortjeneste) for hver uge i den valgte periode."
                    formula="Dækning = Salgspris - Varekøb - Montørløn"
                    details={[
                      "Baseret på afviklede ordrer",
                      "Lilla linje = Gennemsnit for perioden",
                      "Kan filtreres pr. region"
                    ]}
                    dataSource="Data_klar (afviklede ordrer)"
                  />
                }
              >
                <StableChartContainer height={200}>
                  <ComposedChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                    <XAxis dataKey={labelKey} stroke="rgba(255,255,255,0.4)" tick={{ fill: 'rgba(255,255,255,0.6)', fontSize: 10 }} />
                    <YAxis stroke="rgba(255,255,255,0.4)" tick={{ fill: 'rgba(255,255,255,0.6)', fontSize: 10 }} tickFormatter={(v) => `${(v/1000).toFixed(0)}k`} />
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#1A1A1A', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: '#fff' }}
                      labelStyle={{ color: '#fff' }}
                      formatter={(value) => [`${formatDKK(value)} kr`, 'Dækning']}
                    />
                    <ReferenceLine y={averages.daekning} stroke="#8B5CF6" strokeDasharray="4 4" label={{ value: `Gns: ${formatDKK(averages.daekning)}`, fill: '#8B5CF6', fontSize: 10, position: 'left' }} />
                    <Bar dataKey="daekning" fill="#1E6F7A" radius={[4, 4, 0, 0]} name="Dækning" />
                  </ComposedChart>
                </StableChartContainer>
              </SectionCard>

              {/* Dækningsgrad (DG) pr. uge/måned */}
              <SectionCard 
                title={`Dækningsgrad pr. ${periodLabel}`}
                tooltip={
                  <InfoTooltip
                    title="Dækningsgrad (DG)"
                    description="Viser dækningsgraden i procent — hvor stor en andel af omsætningen der udgøres af dækning."
                    formula="DG = (Dækning / Omsætning) × 100"
                    details={[
                      "Omsætning er excl. moms (÷25%)",
                      "Lilla linje = Vægtet gennemsnit for perioden",
                      "Højere DG = bedre rentabilitet"
                    ]}
                    dataSource="Data_klar (dækning + omsætning)"
                  />
                }
              >
                <StableChartContainer height={200}>
                  <ComposedChart data={chartData.map(d => ({
                    ...d,
                    dg: d.omsaetning > 0 ? Math.round((d.daekning / d.omsaetning) * 1000) / 10 : 0
                  }))}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                    <XAxis dataKey={labelKey} stroke="rgba(255,255,255,0.4)" tick={{ fill: 'rgba(255,255,255,0.6)', fontSize: 10 }} />
                    <YAxis stroke="rgba(255,255,255,0.4)" tick={{ fill: 'rgba(255,255,255,0.6)', fontSize: 10 }} tickFormatter={(v) => `${v}%`} domain={[0, 'auto']} />
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#1A1A1A', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: '#fff' }}
                      labelStyle={{ color: '#fff' }}
                      formatter={(value) => [`${value.toFixed(1)}%`, 'Dækningsgrad']}
                    />
                    <ReferenceLine y={Math.round(averages.dg * 10) / 10} stroke="#8B5CF6" strokeDasharray="4 4" label={{ value: `Gns: ${(averages.dg || 0).toFixed(1)}%`, fill: '#8B5CF6', fontSize: 10, position: 'left' }} />
                    <Bar dataKey="dg" fill="#2E7D32" radius={[4, 4, 0, 0]} name="Dækningsgrad">
                      {chartData.map((d, idx) => {
                        const dg = d.omsaetning > 0 ? (d.daekning / d.omsaetning) * 100 : 0;
                        return <Cell key={idx} fill={dg >= 65 ? '#2E7D32' : dg >= 55 ? '#C77700' : '#B3261E'} />;
                      })}
                    </Bar>
                  </ComposedChart>
                </StableChartContainer>
              </SectionCard>

              {/* Marketing Spend */}
              <SectionCard 
                title={`Marketing Spend pr. ${periodLabel}`}
                tooltip={
                  <InfoTooltip
                    title="Marketing Spend pr. uge"
                    description="Viser det samlede marketing spend for hver uge."
                    formula="Spend = Facebook + Google + Andre kanaler"
                    details={[
                      "Inkluderer alle marketingkanaler",
                      "Lilla linje = Gennemsnit for perioden",
                      "Bruges til ROI-beregninger"
                    ]}
                    dataSource="Import_Spend _pr._dag"
                  />
                }
              >
                <StableChartContainer height={200}>
                  <ComposedChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                    <XAxis dataKey={labelKey} stroke="rgba(255,255,255,0.4)" tick={{ fill: 'rgba(255,255,255,0.6)', fontSize: 10 }} />
                    <YAxis stroke="rgba(255,255,255,0.4)" tick={{ fill: 'rgba(255,255,255,0.6)', fontSize: 10 }} tickFormatter={(v) => `${(v/1000).toFixed(0)}k`} />
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#1A1A1A', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: '#fff' }}
                      labelStyle={{ color: '#fff' }}
                      formatter={(value) => [`${formatDKK(value)} kr`, 'Spend']}
                    />
                    <ReferenceLine y={averages.spend} stroke="#8B5CF6" strokeDasharray="4 4" label={{ value: `Gns: ${formatDKK(averages.spend)}`, fill: '#8B5CF6', fontSize: 10, position: 'left' }} />
                    <Bar dataKey="spend" fill="#C77700" radius={[4, 4, 0, 0]} />
                  </ComposedChart>
                </StableChartContainer>
              </SectionCard>

              {/* Nye og Afviklede Bookinger - kombineret */}
              <SectionCard 
                title={`Nye vs. afviklede bookinger pr. ${periodLabel}`}
                tooltip={
                  <InfoTooltip
                    title="Nye vs. afviklede bookinger"
                    description="Sammenligner antal nye bookinger oprettet med antal afviklede opmålinger pr. uge."
                    details={[
                      "Blå linje = Nye bookinger oprettet",
                      "Grøn linje = Afviklede opmålinger",
                      "Mål: 250 nye bookinger pr. uge",
                      "Lilla linje = Gennemsnit nye bookinger"
                    ]}
                    dataSource="Leadvalidator Export + Data_klar"
                  />
                }
              >
                <StableChartContainer height={200}>
                  <ComposedChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                    <XAxis dataKey={labelKey} stroke="rgba(255,255,255,0.4)" tick={{ fill: 'rgba(255,255,255,0.6)', fontSize: 10 }} />
                    <YAxis stroke="rgba(255,255,255,0.4)" tick={{ fill: 'rgba(255,255,255,0.6)', fontSize: 10 }} />
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#1A1A1A', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: '#fff' }}
                      labelStyle={{ color: '#fff' }}
                    />
                    <ReferenceLine y={250} stroke="rgba(255,255,255,0.3)" strokeDasharray="4 4" label={{ value: 'Mål: 250', fill: 'rgba(255,255,255,0.4)', fontSize: 10, position: 'right' }} />
                    <ReferenceLine y={averages.nye_bookinger} stroke="#8B5CF6" strokeDasharray="4 4" label={{ value: `Gns nye: ${Math.round(averages.nye_bookinger)}`, fill: '#8B5CF6', fontSize: 10, position: 'left' }} />
                    <Line type="monotone" dataKey="nye_bookinger" stroke="#1E6F7A" strokeWidth={2} dot={{ fill: '#1E6F7A', r: 4 }} name="Nye bookinger" />
                    <Line type="monotone" dataKey="afviklede_bookinger" stroke="#2E7D32" strokeWidth={2} dot={{ fill: '#2E7D32', r: 4 }} name="Afviklede" />
                  </ComposedChart>
                </StableChartContainer>
              </SectionCard>

              {/* Pris pr. Ny Booking */}
              <SectionCard 
                title={`Pris pr. ny booking pr. ${periodLabel}`}
                tooltip={
                  <InfoTooltip
                    title="Pris pr. ny booking"
                    description="Viser gennemsnitlig marketingpris for at opnå én ny booking."
                    formula="Pris = Marketing spend ÷ Antal nye bookinger"
                    details={[
                      "Grøn: ≤ 1.200 kr (godt)",
                      "Orange: 1.200-1.500 kr (acceptabelt)",
                      "Rød: > 1.500 kr (for dyrt)",
                      "Mål: Under 1.200 kr"
                    ]}
                    dataSource="Beregnet fra spend og bookinger"
                  />
                }
              >
                <StableChartContainer height={200}>
                  <ComposedChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                    <XAxis dataKey={labelKey} stroke="rgba(255,255,255,0.4)" tick={{ fill: 'rgba(255,255,255,0.6)', fontSize: 10 }} />
                    <YAxis stroke="rgba(255,255,255,0.4)" tick={{ fill: 'rgba(255,255,255,0.6)', fontSize: 10 }} />
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#1A1A1A', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: '#fff' }}
                      labelStyle={{ color: '#fff' }}
                      itemStyle={{ color: '#fff' }}
                      formatter={(value) => [`${formatDKK(value)} kr`, 'Pris/ny booking']}
                    />
                    <ReferenceLine y={1200} stroke="rgba(255,255,255,0.3)" strokeDasharray="4 4" label={{ value: 'Mål: 1.200', fill: 'rgba(255,255,255,0.4)', fontSize: 10, position: 'right' }} />
                    <ReferenceLine y={averages.pris_pr_booking} stroke="#8B5CF6" strokeDasharray="4 4" label={{ value: `Gns: ${formatDKK(averages.pris_pr_booking)}`, fill: '#8B5CF6', fontSize: 10, position: 'left' }} />
                    <Bar dataKey="pris_pr_booking" radius={[4, 4, 0, 0]} name="Pris/ny booking">
                      {chartData.map((entry, index) => {
                        let color = '#2E7D32';
                        if (entry.pris_pr_booking > 1500) color = '#B3261E';
                        else if (entry.pris_pr_booking > 1200) color = '#C77700';
                        return <Cell key={`cell-${index}`} fill={color} />;
                      })}
                    </Bar>
                  </ComposedChart>
                </StableChartContainer>
              </SectionCard>

              {/* Pris pr. Afviklet Booking */}
              <SectionCard 
                title={`Pris pr. afviklet booking pr. ${periodLabel}`}
                tooltip={
                  <InfoTooltip
                    title="Pris pr. afviklet booking"
                    description="Viser gennemsnitlig marketingpris pr. faktisk afviklet opmåling."
                    formula="Pris = Marketing spend ÷ Antal afviklede"
                    details={[
                      "Mere præcis end pris pr. ny booking",
                      "Tager højde for aflysninger",
                      "Grøn: ≤ 1.200 kr",
                      "Rød: > 1.500 kr"
                    ]}
                    dataSource="Beregnet fra spend og afviklede"
                  />
                }
              >
                <StableChartContainer height={200}>
                  <ComposedChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                    <XAxis dataKey={labelKey} stroke="rgba(255,255,255,0.4)" tick={{ fill: 'rgba(255,255,255,0.6)', fontSize: 10 }} />
                    <YAxis stroke="rgba(255,255,255,0.4)" tick={{ fill: 'rgba(255,255,255,0.6)', fontSize: 10 }} />
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#1A1A1A', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: '#fff' }}
                      labelStyle={{ color: '#fff' }}
                      itemStyle={{ color: '#fff' }}
                      formatter={(value) => [`${formatDKK(value)} kr`, 'Pris/afviklet']}
                    />
                    <ReferenceLine y={1200} stroke="rgba(255,255,255,0.3)" strokeDasharray="4 4" label={{ value: 'Mål: 1.200', fill: 'rgba(255,255,255,0.4)', fontSize: 10, position: 'right' }} />
                    <ReferenceLine y={averages.pris_pr_afviklet} stroke="#8B5CF6" strokeDasharray="4 4" label={{ value: `Gns: ${formatDKK(averages.pris_pr_afviklet)}`, fill: '#8B5CF6', fontSize: 10, position: 'left' }} />
                    <Bar dataKey="pris_pr_afviklet" radius={[4, 4, 0, 0]} name="Pris/afviklet">
                      {chartData.map((entry, index) => {
                        let color = '#2E7D32';
                        if (entry.pris_pr_afviklet > 1500) color = '#B3261E';
                        else if (entry.pris_pr_afviklet > 1200) color = '#C77700';
                        return <Cell key={`cell-${index}`} fill={color} />;
                      })}
                    </Bar>
                  </ComposedChart>
                </StableChartContainer>
              </SectionCard>

              {/* Aflysningsprocent */}
              <SectionCard 
                title={`Aflysningsprocent pr. ${periodLabel}`}
                tooltip={
                  <InfoTooltip
                    title="Aflysningsprocent"
                    description="Viser andelen af bookinger der blev aflyst i hver uge."
                    formula="Aflysning % = (Aflyste ÷ Totale bookinger) × 100"
                    details={[
                      "Grøn: ≤ 5% (meget godt)",
                      "Orange: 5-10% (acceptabelt)",
                      "Rød: > 10% (problem)",
                      "Mål: Under 10%"
                    ]}
                    dataSource="Bookinger sheet"
                  />
                }
              >
                <StableChartContainer height={200}>
                  <ComposedChart data={cancellationChartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                    <XAxis dataKey={labelKey} stroke="rgba(255,255,255,0.4)" tick={{ fill: 'rgba(255,255,255,0.6)', fontSize: 10 }} />
                    <YAxis stroke="rgba(255,255,255,0.4)" tick={{ fill: 'rgba(255,255,255,0.6)', fontSize: 10 }} domain={[0, 20]} tickFormatter={(v) => `${v}%`} />
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#1A1A1A', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: '#fff' }}
                      labelStyle={{ color: '#fff' }}
                      itemStyle={{ color: '#fff' }}
                      formatter={(value, name) => {
                        if (name === 'cancellation_rate') return [`${value.toFixed(1)}%`, 'Aflysning'];
                        return [value, name];
                      }}
                    />
                    <ReferenceLine y={10} stroke="rgba(255,255,255,0.3)" strokeDasharray="4 4" label={{ value: 'Mål: 10%', fill: 'rgba(255,255,255,0.4)', fontSize: 10, position: 'right' }} />
                    <ReferenceLine y={averages.cancellation_rate} stroke="#8B5CF6" strokeDasharray="4 4" label={{ value: `Gns: ${averages.cancellation_rate?.toFixed(1)}%`, fill: '#8B5CF6', fontSize: 10, position: 'left' }} />
                    <Bar dataKey="cancellation_rate" radius={[4, 4, 0, 0]} name="cancellation_rate">
                      {cancellationChartData.map((entry, index) => {
                        let color = '#2E7D32';
                        if (entry.cancellation_rate > 10) color = '#B3261E';
                        else if (entry.cancellation_rate > 5) color = '#C77700';
                        return <Cell key={`cell-${index}`} fill={color} />;
                      })}
                    </Bar>
                  </ComposedChart>
                </StableChartContainer>
              </SectionCard>

              {/* Gns. Dækning pr. Ordre */}
              <SectionCard 
                title={`Gns. dækning pr. ordre pr. ${periodLabel}`}
                tooltip={
                  <InfoTooltip
                    title="Gns. dækning pr. ordre"
                    description="Viser gennemsnitlig dækning (bruttofortjeneste) pr. afviklet ordre."
                    formula="Gns. dækning = Total dækning ÷ Antal ordrer"
                    details={[
                      "Grøn: ≥ 4.500 kr (godt)",
                      "Orange: 3.500-4.500 kr",
                      "Rød: < 3.500 kr (for lavt)",
                      "Mål: Over 4.500 kr"
                    ]}
                    dataSource="Data_klar (afviklede ordrer)"
                  />
                }
              >
                <StableChartContainer height={200}>
                  <ComposedChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                    <XAxis dataKey={labelKey} stroke="rgba(255,255,255,0.4)" tick={{ fill: 'rgba(255,255,255,0.6)', fontSize: 10 }} />
                    <YAxis stroke="rgba(255,255,255,0.4)" tick={{ fill: 'rgba(255,255,255,0.6)', fontSize: 10 }} tickFormatter={(v) => `${(v/1000).toFixed(0)}k`} />
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#1A1A1A', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: '#fff' }}
                      labelStyle={{ color: '#fff' }}
                      itemStyle={{ color: '#fff' }}
                      formatter={(value) => [`${formatDKK(value)} kr`, 'Gns. dækning/ordre']}
                    />
                    <ReferenceLine y={4500} stroke="rgba(255,255,255,0.3)" strokeDasharray="4 4" label={{ value: 'Mål: 4.500', fill: 'rgba(255,255,255,0.4)', fontSize: 10, position: 'right' }} />
                    <ReferenceLine y={averages.gns_daekning} stroke="#8B5CF6" strokeDasharray="4 4" label={{ value: `Gns: ${formatDKK(averages.gns_daekning)}`, fill: '#8B5CF6', fontSize: 10, position: 'left' }} />
                    <Bar dataKey="gns_daekning" radius={[4, 4, 0, 0]} name="Gns. dækning/ordre">
                      {chartData.map((entry, index) => {
                        let color = '#2E7D32';
                        if (entry.gns_daekning < 3500) color = '#B3261E';
                        else if (entry.gns_daekning < 4500) color = '#C77700';
                        return <Cell key={`cell-${index}`} fill={color} />;
                      })}
                    </Bar>
                  </ComposedChart>
                </StableChartContainer>
              </SectionCard>

              {/* Gns. Dækning pr. Ny Booking */}
              <SectionCard 
                title={`Gns. dækning pr. ny booking pr. ${periodLabel}`}
                tooltip={
                  <InfoTooltip
                    title="Gns. dækning pr. ny booking"
                    description="Viser forventet dækning pr. ny booking baseret på historiske data."
                    formula="Gns. dækning = Total dækning ÷ Nye bookinger"
                    details={[
                      "Kombinerer dækning med booking-antal",
                      "Bruges til forecasting",
                      "Grøn: ≥ 4.500 kr",
                      "Rød: < 3.500 kr"
                    ]}
                    dataSource="Data_klar + Leadvalidator"
                  />
                }
              >
                <StableChartContainer height={200}>
                  <ComposedChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                    <XAxis dataKey={labelKey} stroke="rgba(255,255,255,0.4)" tick={{ fill: 'rgba(255,255,255,0.6)', fontSize: 10 }} />
                    <YAxis stroke="rgba(255,255,255,0.4)" tick={{ fill: 'rgba(255,255,255,0.6)', fontSize: 10 }} tickFormatter={(v) => `${(v/1000).toFixed(0)}k`} />
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#1A1A1A', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: '#fff' }}
                      labelStyle={{ color: '#fff' }}
                      itemStyle={{ color: '#fff' }}
                      formatter={(value) => [`${formatDKK(value)} kr`, 'Gns. dækning/ny booking']}
                    />
                    <ReferenceLine y={4500} stroke="rgba(255,255,255,0.3)" strokeDasharray="4 4" label={{ value: 'Mål: 4.500', fill: 'rgba(255,255,255,0.4)', fontSize: 10, position: 'right' }} />
                    <ReferenceLine y={averages.gns_daekning_pr_ny_booking} stroke="#8B5CF6" strokeDasharray="4 4" label={{ value: `Gns: ${formatDKK(averages.gns_daekning_pr_ny_booking)}`, fill: '#8B5CF6', fontSize: 10, position: 'left' }} />
                    <Bar dataKey="gns_daekning_pr_ny_booking" radius={[4, 4, 0, 0]} name="Gns. dækning/ny booking">
                      {chartData.map((entry, index) => {
                        let color = '#2E7D32';
                        if (entry.gns_daekning_pr_ny_booking < 3500) color = '#B3261E';
                        else if (entry.gns_daekning_pr_ny_booking < 4500) color = '#C77700';
                        return <Cell key={`cell-${index}`} fill={color} />;
                      })}
                    </Bar>
                  </ComposedChart>
                </StableChartContainer>
              </SectionCard>

              {/* Gns. Dækning pr. Afviklet Booking */}
              <SectionCard 
                title={`Gns. dækning pr. afviklet booking pr. ${periodLabel}`}
                tooltip={
                  <InfoTooltip
                    title="Gns. dækning pr. afviklet"
                    description="Viser faktisk dækning pr. afviklet opmåling."
                    formula="Gns. dækning = Total dækning ÷ Afviklede"
                    details={[
                      "Mest præcise dækningsmål",
                      "Tager højde for aflysninger",
                      "Grøn: ≥ 4.500 kr",
                      "Rød: < 3.500 kr"
                    ]}
                    dataSource="Data_klar (afviklede)"
                  />
                }
              >
                <StableChartContainer height={200}>
                  <ComposedChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                    <XAxis dataKey={labelKey} stroke="rgba(255,255,255,0.4)" tick={{ fill: 'rgba(255,255,255,0.6)', fontSize: 10 }} />
                    <YAxis stroke="rgba(255,255,255,0.4)" tick={{ fill: 'rgba(255,255,255,0.6)', fontSize: 10 }} tickFormatter={(v) => `${(v/1000).toFixed(0)}k`} />
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#1A1A1A', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: '#fff' }}
                      labelStyle={{ color: '#fff' }}
                      itemStyle={{ color: '#fff' }}
                      formatter={(value) => [`${formatDKK(value)} kr`, 'Gns. dækning/afviklet']}
                    />
                    <ReferenceLine y={4500} stroke="rgba(255,255,255,0.3)" strokeDasharray="4 4" label={{ value: 'Mål: 4.500', fill: 'rgba(255,255,255,0.4)', fontSize: 10, position: 'right' }} />
                    <ReferenceLine y={averages.gns_daekning_pr_afviklet} stroke="#8B5CF6" strokeDasharray="4 4" label={{ value: `Gns: ${formatDKK(averages.gns_daekning_pr_afviklet)}`, fill: '#8B5CF6', fontSize: 10, position: 'left' }} />
                    <Bar dataKey="gns_daekning_pr_afviklet" radius={[4, 4, 0, 0]} name="Gns. dækning/afviklet">
                      {chartData.map((entry, index) => {
                        let color = '#2E7D32';
                        if (entry.gns_daekning_pr_afviklet < 3500) color = '#B3261E';
                        else if (entry.gns_daekning_pr_afviklet < 4500) color = '#C77700';
                        return <Cell key={`cell-${index}`} fill={color} />;
                      })}
                    </Bar>
                  </ComposedChart>
                </StableChartContainer>
              </SectionCard>

              {/* Konsulent Timer */}
              <SectionCard 
                title={`Konsulent timer pr. ${periodLabel}`}
                tooltip={
                  <InfoTooltip
                    title={`Konsulent timer pr. ${periodLabel}`}
                    description="Viser antal reelle konsulenttimer pr. uge (fratrukket fravær og helligdage)."
                    formula="Timer = business_hours_min / 60 (ekskl. sygdom, ferie, barsel, barns sygedag)"
                    details={[
                      "Konsulent/Montør reduceres med 40%",
                      "Lilla linje = Gennemsnit for perioden",
                      "Bruges i KPT-beregningen"
                    ]}
                    dataSource="CalendarTypeCounts (Data_klar_1)"
                  />
                }
              >
                <StableChartContainer height={200}>
                  <ComposedChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                    <XAxis dataKey={labelKey} stroke="rgba(255,255,255,0.4)" tick={{ fill: 'rgba(255,255,255,0.6)', fontSize: 10 }} />
                    <YAxis stroke="rgba(255,255,255,0.4)" tick={{ fill: 'rgba(255,255,255,0.6)', fontSize: 10 }} />
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#1A1A1A', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: '#fff' }}
                      labelStyle={{ color: '#fff' }}
                      formatter={(value) => [`${formatDKK(value)} timer`, 'Konsulent timer']}
                    />
                    <ReferenceLine y={averages.konsulent_timer} stroke="#8B5CF6" strokeDasharray="4 4" label={{ value: `Gns: ${formatDKK(averages.konsulent_timer)}`, fill: '#8B5CF6', fontSize: 10, position: 'left' }} />
                    <Bar dataKey="timer" fill="#0288D1" radius={[4, 4, 0, 0]} name="Konsulent timer" />
                  </ComposedChart>
                </StableChartContainer>
              </SectionCard>

              {/* DPT pr. uge */}
              <SectionCard 
                title={`DPT (Dækning pr. time) pr. ${periodLabel}`}
                tooltip={
                  <InfoTooltip
                    title="DPT (Dækning pr. time)"
                    description="Viser dækningsbidrag pr. konsulenttime for hver uge."
                    formula="DPT = Dækning ÷ Konsulent timer"
                    details={[
                      "Kombinerer dækning med timer",
                      "Lilla linje = Gennemsnit for perioden",
                      "Højere = bedre udnyttelse"
                    ]}
                    dataSource="Data_klar + CalendarTypeCounts"
                  />
                }
              >
                <StableChartContainer height={200}>
                  <ComposedChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                    <XAxis dataKey={labelKey} stroke="rgba(255,255,255,0.4)" tick={{ fill: 'rgba(255,255,255,0.6)', fontSize: 10 }} />
                    <YAxis stroke="rgba(255,255,255,0.4)" tick={{ fill: 'rgba(255,255,255,0.6)', fontSize: 10 }} tickFormatter={(v) => `${(v/1000).toFixed(0)}k`} />
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#1A1A1A', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: '#fff' }}
                      labelStyle={{ color: '#fff' }}
                      formatter={(value) => [`${formatDKK(value)} kr/t`, 'DPT']}
                    />
                    <ReferenceLine y={averages.dpt} stroke="#8B5CF6" strokeDasharray="4 4" label={{ value: `Gns: ${formatDKK(averages.dpt)}`, fill: '#8B5CF6', fontSize: 10, position: 'left' }} />
                    <Bar dataKey="dpt" fill="#1E6F7A" radius={[4, 4, 0, 0]} name="DPT" />
                  </ComposedChart>
                </StableChartContainer>
              </SectionCard>

              {/* KPT */}
              <SectionCard 
                title={`KPT (Bookinger pr. time) pr. ${periodLabel}`}
                tooltip={
                  <InfoTooltip
                    title="KPT (Kunder pr. time)"
                    description="Viser hvor mange opmålinger der afvikles pr. konsulenttime."
                    formula="KPT = Afviklede opmålinger ÷ Konsulenttimer"
                    details={[
                      "Grøn: ≥ 0,45 (godt)",
                      "Orange: 0,35-0,45",
                      "Rød: < 0,35 (for lavt)",
                      "Mål: Over 0,45"
                    ]}
                    dataSource="CalendarTypeCounts + Data_klar"
                  />
                }
              >
                <StableChartContainer height={200}>
                  <ComposedChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                    <XAxis dataKey={labelKey} stroke="rgba(255,255,255,0.4)" tick={{ fill: 'rgba(255,255,255,0.6)', fontSize: 10 }} />
                    <YAxis stroke="rgba(255,255,255,0.4)" tick={{ fill: 'rgba(255,255,255,0.6)', fontSize: 10 }} domain={[0, 1]} />
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#1A1A1A', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: '#fff' }}
                      labelStyle={{ color: '#fff' }}
                      formatter={(value) => [value.toFixed(2), 'KPT']}
                    />
                    <ReferenceLine y={0.45} stroke="rgba(255,255,255,0.3)" strokeDasharray="4 4" label={{ value: 'Mål: 0.45', fill: 'rgba(255,255,255,0.4)', fontSize: 10, position: 'right' }} />
                    <ReferenceLine y={averages.kpt} stroke="#8B5CF6" strokeDasharray="4 4" label={{ value: `Gns: ${averages.kpt?.toFixed(2)}`, fill: '#8B5CF6', fontSize: 10, position: 'left' }} />
                    <Line type="monotone" dataKey="kpt" stroke="#1E6F7A" strokeWidth={2} dot={{ fill: '#1E6F7A', r: 4 }} name="KPT" />
                  </ComposedChart>
                </StableChartContainer>
              </SectionCard>

            </div>

            {/* Data Table */}
            <SectionCard title={viewMode === 'uge' ? 'Ugentlig data' : 'Månedlig data'}>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-white/60 border-b border-white/10">
                      <th className="text-left py-2 px-2">{viewMode === 'uge' ? 'Uge' : 'Måned'}</th>
                      <th className="text-right py-2 px-2">Spend</th>
                      <th className="text-right py-2 px-2">Nye</th>
                      <th className="text-right py-2 px-2">Afviklede</th>
                      <th className="text-right py-2 px-2">Pris/book.</th>
                      <th className="text-right py-2 px-2">Aflys. %</th>
                      <th className="text-right py-2 px-2">Dækning</th>
                      <th className="text-right py-2 px-2">K. timer</th>
                      <th className="text-right py-2 px-2">DPT</th>
                      <th className="text-right py-2 px-2">Gns. dæk.</th>
                      <th className="text-right py-2 px-2">KPT</th>
                    </tr>
                  </thead>
                  <tbody>
                    {chartData.map((row, idx) => (
                      <tr key={row.week_key || row.month_key} className={`border-b border-white/5 ${idx % 2 === 0 ? 'bg-white/[0.02]' : ''}`}>
                        <td className="py-2 px-2 text-white">{row[labelKey]}</td>
                        <td className="py-2 px-2 text-right text-[#C77700]">{formatDKK(row.spend)}</td>
                        <td className="py-2 px-2 text-right text-white">{row.nye_bookinger}</td>
                        <td className="py-2 px-2 text-right text-white">{row.afviklede_bookinger}</td>
                        <td className="py-2 px-2 text-right" style={{ color: row.pris_pr_booking <= 1200 ? '#2E7D32' : row.pris_pr_booking <= 1500 ? '#C77700' : '#B3261E' }}>
                          {formatDKK(row.pris_pr_booking)}
                        </td>
                        <td className="py-2 px-2 text-right" style={{ color: row.aflysning_pct <= 5 ? '#2E7D32' : row.aflysning_pct <= 10 ? '#C77700' : '#B3261E' }}>
                          {row.aflysning_pct.toFixed(1)}%
                        </td>
                        <td className="py-2 px-2 text-right text-[#1E6F7A]">{formatDKK(row.daekning)}</td>
                        <td className="py-2 px-2 text-right text-[#0288D1]">{row.timer?.toFixed(1) || '0'}</td>
                        <td className="py-2 px-2 text-right text-[#1E6F7A]">{formatDKK(row.dpt || 0)}</td>
                        <td className="py-2 px-2 text-right" style={{ color: row.gns_daekning >= 4500 ? '#2E7D32' : row.gns_daekning >= 3500 ? '#C77700' : '#B3261E' }}>
                          {formatDKK(row.gns_daekning)}
                        </td>
                        <td className="py-2 px-2 text-right" style={{ color: row.kpt >= 0.45 ? '#2E7D32' : row.kpt >= 0.35 ? '#C77700' : '#B3261E' }}>
                          {row.kpt.toFixed(2)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </SectionCard>
          </>
        )}
      </main>

      {/* Footer */}
      <footer className="border-t border-white/5 py-4 mt-8">
        <div className="max-w-[1920px] mx-auto px-6">
          <p className="text-center text-xs text-white/60">
            Luxsun Nordic · Historisk Data · {new Date().getFullYear()}
          </p>
        </div>
      </footer>
    </div>
  );
};
